import { AliasToken } from 'antd/es/theme/interface';
import {
  TitleKey,
  TitleKeyCompliance,
  TitleKeyComplianceScreens,
  TitleKeyInternalControls
} from './Management/types';

// Extend AliasToken to include custom theme properties
export interface CustomAliasToken extends AliasToken {
  tableBorderColor?: string;
}

export interface IToken {
  token: CustomAliasToken;
}

export interface IStyledProps extends IToken {
  scheme?: string;
}

export interface IItemWidth extends IToken {
  width?: number;
  scheme?: string;
}

export interface IThemeProps {
  scheme?: string;
}

export interface IFilters {
  value: string[];
  filter: string;
  options: ISelectionOption[];
}

export interface ISelectionOption {
  value: string;
  label: string;
}

export interface ICategoryResponse {
  id: number;
  short_name: string;
  description: string;
  name?: string;
}

export interface ICategoryOption {
  label: string;
  value: string | number;
}

export interface IResetPass {
  new_password: string;
}

export interface ICustomMenuItem {
  label: JSX.Element;
  key: string;
  path: string;
}

export interface IResponseFilters {
  ind?: number;
  cntry?: number;
  mod?: number;
  reg?: number;
  ent?: number;
  cat?: number;
  prod?: number;
}

export interface IUserInfo {
  email: string;
  entitlements: string[];
  role: string;
  user_id: number;
}

export interface IOwnership {
  per_email: string;
  per_first_name: string;
  per_last_name: string;
  per_person_id: number;
}

export interface IWorkingArea {
  area: string;
  infoText: string;
  imgSrc: string;
  href: string;
  slug?: string;
}

export interface IWhoAmILayoutProps {
  children: React.ReactNode;
  onFiltersChange?: (filters: any) => void;
  onDisplay?: () => void;
  onSave?: () => void;
  saveDisabled?: boolean;
}

export interface UseUnsavedChangesOptions {
  hasChanges: boolean;
  data?: unknown;
  initialData?: unknown;
  modalTitle?: string;
  modalContent?: string;
  okText?: string;
  cancelText?: string;
}

export enum ThemeList {
  DEEP_BLUE_THEME = 'deepBlueTheme',
  DEEP_NAVY_THEME = 'deepNavyTheme',
  DARK_UI_THEME = 'darkUITheme',
  LIGHT_UI_THEME = 'lightUITheme',
  PINK_UI_THEME = 'pinkUITheme',
  BLUISH_THEME = 'bluishTheme',
  LILAC_THEME = 'lilacTheme',
  TURQUOISE_THEME = 'turquoiseTheme'
}

export enum LanguageList {
  EN = 'en',
  DE = 'de',
  FR = 'fr',
  IT = 'it',
  ES = 'es',
  PT = 'pt'
}

export enum TitleListEN {
  WORKING_AREAS = 'Working areas',
  COMP_EST = 'Compliance Management Hub',
  DATA_MANAGEMENT = 'Data Management',
  REPORTING = 'Reports',
  LIBRARY = 'Library',
  INTERNAL_CONTROLS = 'Business Process & Risk Control Hub',
  AUDIT_FINDINGS = 'Audit findings',
  CATEGORY = 'Category Management',
  PRODUCT = 'Product Management',
  SOLUTION_TYPE = 'Solution Type Management',
  TASK_TYPE = 'Task Type Management',
  DEFINE_CLIENT_REQ = 'Baseline Definition',
  ADD_GAPS_AND_AUDIT_FINDINGS = 'Gaps and Audit Finding Management',
  SOLUTION_DEFINITIONS = 'Solution Definition',
  TASKS_DEFINITIONS = 'Task Definition',
  EVIDENCE_DEFINITIONS = 'Evidence Definition',
  CONTROL_REGISTER = 'Control Register Maintenance (1st Line of Defense)',
  RISK_REGISTER = 'Risk Register Maintenance',
  BUSINESS_PROCESSES = 'Business Process Level 4',
  BUSINESS_PROCESSESL1 = 'Business Process Level 1',
  BUSINESS_PROCESSESL2 = 'Business Process Level 2',
  BUSINESS_PROCESSESL3 = 'Business Process Level 3',
  BUSINESS_PROCESS_MAPPING = 'Control and Risk Allocation to Business Processes',
  SUPER_ADMIN = 'Super user',
  CONTROLS_TEST = 'Operational Effectiveness Testing (2nd Line of Defense)',
  CONTROLS_TEST_DESIGN = 'Design Effectiveness Testing (2nd Line of Defense)',
  ALLOCATION = 'Allocation of division, legal entities, business units and region',
  PROCESS_ALLOCATION = 'Allocation of Processes to Entities',
  CONTROL_ATTESTATION = 'Control Attestation (1st Line of Defense)',
  DEPENDENCY_TREE = 'Dependency Tree'
}

export enum TitleListDE {
  WORKING_AREAS = 'Arbeitsbereiche',
  COMP_EST = 'Workflow von Regeln zu Nachweisen',
  DATA_MANAGEMENT = 'Datenmanagement',
  REPORTING = 'Berichterstattung',
  LIBRARY = 'Bibliothek',
  INTERNAL_CONTROLS = 'Steuerung und Risikozuweisung',
  AUDIT_FINDINGS = 'Prüfungsfeststellungen',
  CATEGORY = 'Kategorienverwaltung',
  PRODUCT = 'Produktverwaltung',
  SOLUTION_TYPE = 'Lösungstyp-Verwaltung',
  TASK_TYPE = 'Aufgabentyp-Verwaltung',
  DEFINE_CLIENT_REQ = 'Kundenanforderung definieren',
  ADD_GAPS_AND_AUDIT_FINDINGS = 'Gaps und Prüfungsfeststellungen hinzufügen',
  SOLUTION_DEFINITIONS = 'Lösungsdefinition',
  EVIDENCE_DEFINITIONS = 'Nachweisdefinitionen',
  TASKS_DEFINITIONS = 'Aufgabendefinition',
  CONTROL_REGISTER = 'Kontrollregister',
  RISK_REGISTER = 'Risikoregister',
  BUSINESS_PROCESSES = 'Geschäftsprozesse 1',
  BUSINESS_PROCESSESL1 = 'Geschäftsprozesse 2',
  BUSINESS_PROCESSESL2 = 'Geschäftsprozesse 3',
  BUSINESS_PROCESSESL3 = 'Geschäftsprozesse 4',
  BUSINESS_PROCESS_MAPPING = 'Geschäftsprozess-Mapping',
  CONTROLS_TEST = 'Wartung des Kontrolltestregisters',
  CONTROLS_TEST_DESIGN = 'Prüfung der Designwirksamkeit (2. Verteidigungslinie)',
  ALLOCATION = 'Zuordnung von Divisionen, Rechtseinheiten, Geschäftseinheiten und Regionen',
  PROCESS_ALLOCATION = 'Zuordnung von Prozessen zu Entitäten',
  CONTROL_ATTESTATION = 'Kontrollbestätigung (1. Verteidigungslinie)',
  DEPENDENCY_TREE = 'Abhängigkeitsbaum'
}

export enum TitleListES {
  WORKING_AREAS = 'Áreas de Trabajo',
  COMP_EST = 'Creación de Cumplimiento',
  DATA_MANAGEMENT = 'Gestión de Datos',
  REPORTING = 'Informe',
  LIBRARY = 'Biblioteca',
  INTERNAL_CONTROLS = 'Controles y Asignación de Riesgos',
  AUDIT_FINDINGS = 'Gestión de Hallazgos de Auditoría',
  CATEGORY = 'Gestión de Categorías',
  PRODUCT = 'Gestión de Productos',
  SOLUTION_TYPE = 'Gestión de Tipo de Solución',
  TASK_TYPE = 'Gestión de Tipo de Tarea',
  DEFINE_CLIENT_REQ = 'Definir requisito del cliente',
  ADD_GAPS_AND_AUDIT_FINDINGS = 'Agregar brechas y hallazgos de auditoría',
  SOLUTION_DEFINITIONS = 'Definición de solución',
  TASKS_DEFINITIONS = 'Definición de evidencia',
  EVIDENCE_DEFINITIONS = 'Definición de tareas',
  CONTROL_REGISTER = 'Registro de controles',
  RISK_REGISTER = 'Registro de riesgos',
  BUSINESS_PROCESSES = 'Procesos de negocio',
  BUSINESS_PROCESSESL1 = 'Procesos de negocio 1',
  BUSINESS_PROCESSESL2 = 'Procesos de negocio 2',
  BUSINESS_PROCESSESL3 = 'Procesos de negocio 3',
  BUSINESS_PROCESS_MAPPING = 'Mapeo de procesos de negocio',
  CONTROLS_TEST = 'Mantenimiento del registro de pruebas de control',
  CONTROLS_TEST_DESIGN = 'Prueba de efectividad del diseño (2ª línea de defensa)',
  ALLOCATION = 'Asignación de divisiones, entidades legales, unidades de negocio y regiones',
  PROCESS_ALLOCATION = 'Asignación de procesos a entidades',
  CONTROL_ATTESTATION = 'Certificación de control (1ª línea de defensa)',
  DEPENDENCY_TREE = 'Árbol de dependencias'
}

export enum TitleListFR {
  WORKING_AREAS = 'Domaines de travail',
  COMP_EST = 'Flux de travail des règles vers les preuves',
  DATA_MANAGEMENT = 'Gestion des données',
  REPORTING = 'Rapports',
  LIBRARY = 'Bibliothèque',
  INTERNAL_CONTROLS = 'Contrôles et Allocation des Risques',
  AUDIT_FINDINGS = 'Gestion des constats d’audit',
  CATEGORY = 'Gestion des catégories',
  PRODUCT = 'Gestion des produits',
  SOLUTION_TYPE = 'Gestion du Type de Solution',
  TASK_TYPE = 'Gestion du Type de Tâche',
  DEFINE_CLIENT_REQ = 'Définir les besoins du client',
  ADD_GAPS_AND_AUDIT_FINDINGS = "Ajouter des lacunes et des constatations d'audit",
  SOLUTION_DEFINITIONS = 'Définition de la solution',
  TASKS_DEFINITIONS = 'Définition de la preuve',
  EVIDENCE_DEFINITIONS = 'Définition des tâches',
  CONTROL_REGISTER = 'Registre des contrôles',
  RISK_REGISTER = 'Registre des risques',
  BUSINESS_PROCESSES = 'Processus métier',
  BUSINESS_PROCESSESL1 = 'Processus métier 1',
  BUSINESS_PROCESSESL2 = 'Processus métier 2',
  BUSINESS_PROCESSESL3 = 'Processus métier 3',
  BUSINESS_PROCESS_MAPPING = 'Cartographie des processus métier',
  CONTROLS_TEST = 'Maintenance du registre des tests de contrôle',
  CONTROLS_TEST_DESIGN = "Test d'efficacité de la conception (2ème ligne de défense)",
  ALLOCATION = 'Affectation des divisions, entités juridiques, unités commerciales et régions',
  PROCESS_ALLOCATION = 'Affectation des processus aux entités',
  CONTROL_ATTESTATION = 'Attestation de contrôle (1ère ligne de défense)',
  DEPENDENCY_TREE = 'Arbre de dépendance'
}

export enum TitleListIT {
  WORKING_AREAS = 'Aree di lavoro',
  COMP_EST = 'Creazione di conformità',
  DATA_MANAGEMENT = 'Gestione dei dati',
  REPORTING = 'Segnalazione',
  LIBRARY = 'Biblioteca',
  INTERNAL_CONTROLS = 'Controlli e Allocazione del Rischio',
  AUDIT_FINDINGS = 'Gestione delle constatazioni dell’audit',
  CATEGORY = 'Gestione delle categorie',
  PRODUCT = 'Gestione dei prodotti',
  SOLUTION_TYPE = 'Gestione del Tipo di Soluzione',
  TASK_TYPE = 'Gestione del Tipo di Attività',
  DEFINE_CLIENT_REQ = 'Definisci requisito del cliente',
  ADD_GAPS_AND_AUDIT_FINDINGS = 'Aggiungi lacune e risultati di audit',
  SOLUTION_DEFINITIONS = 'Definizione della soluzione',
  TASKS_DEFINITIONS = 'Definizione delle evidenze',
  EVIDENCE_DEFINITIONS = 'Definizione dei compiti',
  CONTROL_REGISTER = 'Registro dei controlli',
  RISK_REGISTER = 'Registro dei rischi',
  BUSINESS_PROCESSES = 'Processi aziendali',
  BUSINESS_PROCESSESL1 = 'Processi aziendali 1',
  BUSINESS_PROCESSESL2 = 'Processi aziendali 2',
  BUSINESS_PROCESSESL3 = 'Processi aziendali 3',
  BUSINESS_PROCESS_MAPPING = 'Mappatura dei processi aziendali',
  CONTROLS_TEST = 'Manutenzione del registro dei test di controllo',
  CONTROLS_TEST_DESIGN = 'Test di efficacia del design (2ª linea di difesa)',
  ALLOCATION = 'Assegnazione di divisioni, entità legali, unità aziendali e regioni',
  PROCESS_ALLOCATION = 'Assegnazione di processi alle entità',
  CONTROL_ATTESTATION = 'Attestazione di controllo (1ª linea di difesa)',
  DEPENDENCY_TREE = 'Albero delle dipendenze'
}

export enum TitleListPT {
  WORKING_AREAS = 'Áreas de Trabalho',
  COMP_EST = 'Criação de Conformidade',
  DATA_MANAGEMENT = 'Gestão de Dados',
  REPORTING = 'Relatórios',
  LIBRARY = 'Biblioteca',
  INTERNAL_CONTROLS = 'Controles e Alocação de Riscos',
  AUDIT_FINDINGS = 'Gestão de Constatacoes da Auditoria',
  CATEGORY = 'Gestão de Categorias',
  PRODUCT = 'Gestão de Produtos',
  SOLUTION_TYPE = 'Gestão de Tipo de Solução',
  TASK_TYPE = 'Gestão de Tipo de Tarefa',
  DEFINE_CLIENT_REQ = 'Definir requisito do cliente',
  ADD_GAPS_AND_AUDIT_FINDINGS = 'Adicionar lacunas e descobertas de auditoria',
  SOLUTION_DEFINITIONS = 'Definição de solução',
  TASKS_DEFINITIONS = 'Definição de evidência',
  EVIDENCE_DEFINITIONS = 'Definição de tarefas',
  CONTROL_REGISTER = 'Registro de controles',
  RISK_REGISTER = 'Registro de riscos',
  BUSINESS_PROCESSES = 'Processos de negócio',
  BUSINESS_PROCESSESL1 = 'Processos de negócio 1',
  BUSINESS_PROCESSESL2 = 'Processos de negócio 2',
  BUSINESS_PROCESSESL3 = 'Processos de negócio 3',
  BUSINESS_PROCESS_MAPPING = 'Mapeamento de processos de negócio',
  CONTROLS_TEST = 'Manutenção do registro de testes de controle',
  CONTROLS_TEST_DESIGN = 'Teste de eficácia do design (2ª linha de defesa)',
  ALLOCATION = 'Alocação de divisões, entidades legais, unidades de negócio e regiões',
  PROCESS_ALLOCATION = 'Alocação de processos a entidades',
  CONTROL_ATTESTATION = 'Atestado de controle (1ª linha de defesa)',
  DEPENDENCY_TREE = 'Árvore de dependências'
}

export enum AreasDe {
  DATA_MANAGEMENT = 'Datenmanagement',
  COMP_EST = 'Compliance-Einrichtung',
  COCKPIT = 'Cockpit',
  RISK_MANAGEMENT = 'Risiko- und Kontrollmanagement',
  REPORTING = 'Berichterstattung',
  LIBRARY = 'Bibliothek',
  INTERNAL_CONTROLS = 'Internal Controls'
}

export enum AreasEn {
  DATA_MANAGEMENT = 'Data Management',
  COMP_EST = 'Workflow',
  COCKPIT = 'Cockpit',
  RISK_MANAGEMENT = 'Risk and Control Management',
  REPORTING = 'Reporting',
  LIBRARY = 'Library',
  INTERNAL_CONTROLS = 'Interne Kontrollen'
}

export type TitleList =
  | TitleListEN
  | TitleListDE
  | TitleListES
  | TitleListFR
  | TitleListIT
  | TitleListPT;

// CategoryList is now dynamic - use getDynamicCategories() from constants/categorieNames
// This enum is deprecated but kept for backward compatibility
export enum CategoryList {}
// Dynamic categories are fetched from API

export enum YesNo {
  YES = 'Y',
  NO = 'N'
}

export enum Lang {
  en = 'en',
  ge = 'ge'
}

export const TitleLists: Record<string, Record<TitleKey, string>> = {
  en: TitleListEN,
  de: TitleListDE,
  es: TitleListES,
  fr: TitleListFR,
  it: TitleListIT,
  pt: TitleListPT
};

export const TitleListsCompliance: Record<
  string,
  Record<TitleKeyCompliance, string>
> = {
  en: TitleListEN,
  de: TitleListDE,
  es: TitleListES,
  fr: TitleListFR,
  it: TitleListIT,
  pt: TitleListPT
};

export const TitleListsComplianceScreens: Record<
  string,
  Record<TitleKeyComplianceScreens, string>
> = {
  en: TitleListEN,
  de: TitleListDE,
  es: TitleListES,
  fr: TitleListFR,
  it: TitleListIT,
  pt: TitleListPT
};

export const TitleListsInternalControls: Record<
  string,
  Record<TitleKeyInternalControls, string>
> = {
  en: TitleListEN,
  de: TitleListDE,
  es: TitleListES,
  fr: TitleListFR,
  it: TitleListIT,
  pt: TitleListPT
};
